import { Component, Input, OnDestroy, Output, EventEmitter, ViewChild,
    TemplateRef, OnInit, OnChanges, SimpleChanges, AfterViewInit, ElementRef  } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { Observable, Subscription, } from 'rxjs';
import { isArray } from 'underscore';
import { isEqual } from 'lodash';

import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { AutocompleteCustomOption, AppearCondition } from './autocomplete-custom-option.model';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { PrimeAutocompleteComponent } from '../../../Forms/components/PrimeAutocomplete/autocomplete';

/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the autocomplete has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***ariaInvalid*** - use this to indicate state if the autocomplete can fail in certain scenario
 * + ***ariaLabel*** - use this to provide an accessible label for the autocomplete. Don't use this if ariaLabelledBy is used
 * + ***ariaLabelledBy*** - use this to associate an external label with the autocomplete. Use id of the label as this value. This overwrites ariaLabel
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***isInTable*** - use this to handle focusability if the autocomplete is in a table
 */
@Component ({
    selector: 'agpr-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    providers: [getProvider(AutocompleteComponent)],
    standalone: false
})
export class AutocompleteComponent extends ControlValueAccessorModel<any> implements OnInit, OnDestroy, OnChanges, AfterViewInit {

    @Input() id: string;
    @Input() dropdown: boolean = false;
    @Input() dropdownMode: string = 'blank';
    @Input() labelField: string;
    @Input() label: string;
    @Input() placeholder: string;
    @Input() ariaLabel: string;
    @Input() ariaLabelledBy: string;
    @Input() styleClass: string;
    @Input() disabled: boolean = false;
    @Input() sortSuggestions: boolean = true;
    @Input() multiSelection: boolean = false;
    @Input() autoHighlight: boolean = false;
    @Input() forceClearAfterSelection: boolean = false;
    @Input() panelStyleClass: string = '';
    @Input() forceSelection: boolean = null;
    @Input() minLength: number = 3;
    @Input() maxLength: number = 64;
    @Input() delay: number = 300;
    @Input() itemTemplate: TemplateRef<any> = null;
    @Input() icon: string;
    @Input() clearOnBlur: boolean = true;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.default;
    @Input() required: boolean = false;
    @Input() ariaInvalid: boolean = false;
    @Input() ariaDescribedBy: string = '';
    @Input() isInTable: boolean = false;
    @Input() tooltipInfo: string;
    @Input() showHiddenOption: boolean = false;
    @Input() inputGroupLeft: boolean = false;
    @Input() flatBottomRight: boolean = false;

    // passed in values so the results from suggestionFetcher can check for duplicates
    @Input() valueForSuggestions: any;

    // We compare the whole selected item object when hiding it from the options
    // Use this field to indicate which part of the object to compare only
    @Input() selectedItemsHideKey: string = null;

    // Pass some special options to appear alongside the suggestions
    // They will invoke their specific action() when selected
    @Input() customOptions: AutocompleteCustomOption[] = [];

    // Use when a backend call is required for each new query string
    // i.e. geography autocomplete
    @Input() suggestionsFetcher: (query: string) => Observable<any[]>;

    // Use when all the options are known upfront
    // i.e. list of user's folders
    @Input() set suggestions(theSuggestions: Observable<any[]>) {
        if (this.suggestionSubscription) {
            this.suggestionSubscription.unsubscribe();
        }
        this.suggestionSubscription = theSuggestions.subscribe(x => {
            this.unfilteredItems = x || [];

            if (this.sortSuggestions && this.unfilteredItems.length > 0) {
                this.unfilteredItems.sort((a, b) => {
                    const nameOfA = a[this.labelField]?.toLowerCase();
                    const nameOfB = b[this.labelField]?.toLowerCase();

                    return nameOfA < nameOfB ? -1 : nameOfA > nameOfB ? 1 : 0;
                });
            }
        });
    }

    @Output() itemSelected: EventEmitter<any> = new EventEmitter();
    @Output() keyup: EventEmitter<any> = new EventEmitter();
    @Output() clear: EventEmitter<void> = new EventEmitter();
    @Output() searchStringChanged: EventEmitter<string> = new EventEmitter();
    @Output() show: EventEmitter<any> = new EventEmitter();
    @Output() hide: EventEmitter<any> = new EventEmitter();
    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter();
    @Output() focus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

    @ViewChild('autocomplete') autocomplete: PrimeAutocompleteComponent;
    @ViewChild('ariaLiveRegion') ariaLiveRegion: ElementRef;
    @ViewChild('showHiddenItem') showHiddenItem: TemplateRef<any>;


    filteredItems: any[] = [];
    unfilteredItems: any[] = [];
    showHidden: boolean = false;
    hiddenCount: number;
    currentSearchString: string;

    selectedItem: any;
    selectedItemFromDropdown: any;


    readonly showHiddenCustomOption: AutocompleteCustomOption = {
        action: () => {
            this.toggleHiddenItems();
        },
        appearCondition: AppearCondition.always,
        clearOnSelect: false,
        labelTemplateVariable: this,
        showDivider: true
    };
    readonly agprComponentStyle = AgprComponentStyleEnum;

    private suggestionSubscription: Subscription;
    private filterTimeout;
    private highlightedOptionIndex: number;
    private scrollPanel: Element;

    constructor(
        public domSanitizer: DomSanitizer,
        public localizationService: LocalizationService
    ) {
        super();
        this.selectedItem = '';
    }

    ngOnInit(): void {
        if (this.style === AgprComponentStyleEnum.social || this.style === AgprComponentStyleEnum.socialPlus ) {
            this.panelStyleClass = this.panelStyleClass + ' agpr-autocomplete-panel--social';
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.autocomplete) {
            if (changes.hasOwnProperty('ariaInvalid')) {
                this.autocomplete.inputEL.nativeElement.setAttribute('aria-invalid', this.ariaInvalid);
            }
            if (changes.hasOwnProperty('ariaDescribedBy')) {
                this.autocomplete.inputEL.nativeElement.setAttribute('aria-describedby', this.ariaDescribedBy);
            }
        }
    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.autocomplete.inputEL.nativeElement.setAttribute('aria-invalid', this.ariaInvalid);
            if (this.ariaDescribedBy) {
                this.autocomplete.inputEL.nativeElement.setAttribute('aria-describedby', this.ariaDescribedBy);
            }
            this.autocomplete.dropdownButton?.nativeElement.setAttribute('tabindex', '-1');
            this.autocomplete.dropdownButton?.nativeElement.setAttribute('aria-expanded', 'false');
            if (this.isInTable) {
                this.autocomplete.inputEL?.nativeElement.setAttribute('tabindex', '-1');
            }
            if (this.showHiddenOption) {
                this.showHiddenCustomOption.labelTemplate = this.showHiddenItem;
            }
        });
    }

    toggleHiddenItems(event?: MouseEvent) {
        event?.stopImmediatePropagation();
        this.showHidden = !this.showHidden;
        this.performAutocomplete(this.currentSearchString);
    }

    writeValue(value: any | any[]): void {
        if (this.multiSelection) {
            this.value = value;
        } else {
            this.selectedItem = value;
            this.selectedItemFromDropdown = this.selectedItem;
        }
    }

    performAutocomplete(searchString: string) {
        this.currentSearchString = searchString;
        this.searchStringChanged.emit(searchString);

        if (this.suggestionsFetcher) {
            this.suggestionsFetcher(searchString.replace(/"/g, '')).subscribe(results => {
                results = results || [];
                if (this.autocomplete.inputEL.nativeElement.value?.length >= this.minLength || this.dropdown) {
                    this.filteredItems = results.slice();
                    // No need to filter here since we assume the fetcher only returns relevant suggestions
                    this.processSuggestions(searchString);
                } else {
                    this.filteredItems = [];
                }
            }, error => {
                this.filteredItems = [];
            });
        } else {
            if (!searchString) {
                // dropdown was clicked, restore unfiltered items
                this.filteredItems = this.unfilteredItems?.slice();
                this.processSuggestions(searchString);

                return;
            }

            if (this.filterTimeout) {
                clearTimeout(this.filterTimeout);
            }

            this.filterTimeout = setTimeout(
                () => {
                    this.filteredItems = this.unfilteredItems?.filter(item =>
                        !!item[this.labelField] && item[this.labelField].toLocaleLowerCase().indexOf(searchString.toLocaleLowerCase()) > -1
                    ) || [];
                    this.processSuggestions(searchString);
                }, 300
            );
        }
    }

    processSuggestions(searchString: string) {
        if (this.showHiddenOption) {
            this.hiddenCount = this.filteredItems.filter(item => item.hidden || item.value?.hidden).length;
            if (!this.showHidden) {
                this.filteredItems = this.filteredItems.filter(item => !item.hidden && !item.value?.hidden);
            }
        }
        const customOptionsNumber = this.addCustomOptions(searchString);
        // Disallow selection of already selected items
        if (this.multiSelection) {
            this.filteredItems = this.filteredItems.filter(item => {
                return !this.value.find(selectedItem => {
                    if (this.selectedItemsHideKey) {
                        return selectedItem[this.selectedItemsHideKey] === item[this.selectedItemsHideKey];
                    }
                    return selectedItem === item;
                });
            });
        } else if (
            this.valueForSuggestions &&
            isArray(this.valueForSuggestions) &&
            this.valueForSuggestions.length >= 0) {

            this.filteredItems = this.filteredItems.filter(item => {
                return !this.valueForSuggestions.find(selectedItem => {
                    if (this.selectedItemsHideKey) {
                        return selectedItem[this.selectedItemsHideKey] === item[this.selectedItemsHideKey];
                    }
                    return selectedItem === item;
                });
            });
        }
        if (this.ariaLiveRegion) {
            this.updateResultAnnouncement(customOptionsNumber);
        }
    }

    updateResultAnnouncement(customOptionsNumber: number): void {
        const updateElement = document.createElement('DIV');
        updateElement.textContent = this.localizationService.transform('Common.autocomplete.resultAnnouncement', {
            number: this.filteredItems.length - customOptionsNumber > 0 ? this.filteredItems.length - customOptionsNumber : 'no'
        });
        (this.ariaLiveRegion.nativeElement as HTMLElement).appendChild(updateElement);
        setTimeout(() => {
            (this.ariaLiveRegion.nativeElement as HTMLElement).removeChild(updateElement);
        }, 300);
    }

    addCustomOptions(searchString: string): number {
        if ((!this.customOptions || this.customOptions.length === 0) && !this.showHiddenOption) {
            return 0;
        }

        let customOptionsNumber = 0;
        if (this.showHiddenOption && this.hiddenCount && !this.filteredItems.includes(this.showHiddenCustomOption)) {
            this.filteredItems.unshift(this.showHiddenCustomOption);
            customOptionsNumber++;
        }

        const noSuggestions = this.filteredItems.length === 0;
        const noSuggestionMatchingSearchString = searchString.length > 0 && this.filteredItems
            .filter(suggestion => {
                return (<string>suggestion[this.labelField])?.toLowerCase() === searchString.toLowerCase();
            })
            .length === 0;

        // We're inserting with unshift so
        // reverse so we keep the order that the caller has wanted
        for (let i = this.customOptions.length - 1; i >= 0; i--) {
            const customOption = this.customOptions[i];
            if ((customOption.appearCondition === AppearCondition.always) ||
                (customOption.appearCondition === AppearCondition.noSuggestions && noSuggestions) ||
                (customOption.appearCondition === AppearCondition.noExactMatchInSuggestions && noSuggestionMatchingSearchString)) {

                // Add this to the option or it's not really trackable outside
                // of the autocomplete's completeMethod event
                customOption.searchString = searchString;
                this.filteredItems.unshift(customOption);
                customOptionsNumber++;
            }
        }

        return customOptionsNumber;
    }

    onItemSelect(selectedItem: any) {
        this.selectedItemFromDropdown = selectedItem;

        if (selectedItem.action) {
            const inputValue = this.autocomplete.inputValue;
            const searchString = selectedItem.searchString;
            let inputString = '';

            if (selectedItem?.preferSearchkeyword) {
                inputString = searchString;
            } else {
                inputString = searchString !== inputValue ? inputValue : searchString;
            }

            selectedItem.searchString = inputString;
            selectedItem.action(inputString);

        } else {
            if (this.multiSelection) {
                if (!this.value.find(x => x === selectedItem)) {
                    this.value.push(selectedItem);
                    this.onModelChange(this.value);
                }
                this.autocomplete.writeValue(null);
            } else {
                this.onModelChange(selectedItem);
            }
            this.itemSelected.emit(selectedItem);
        }
        if (selectedItem.clearOnSelect || this.forceClearAfterSelection) {
            this.clearInput();
        }
    }

    ngOnDestroy() {
        if (this.suggestionSubscription) {
            this.suggestionSubscription.unsubscribe();
        }
    }

    onInputBlur(event?: Event) {
        event.preventDefault();
        this.onModelTouched();

        if (this.clearOnBlur) {
            this.clearInput();
        } else if (this.forceSelection === false) {
            this.onModelChange(this.multiSelection ? this.value : this.selectedItem);
        }
        this.blur.emit();
    }

    onInputFocus(event?: Event) {
        this.focus.emit();
    }

    clearInput() {
        this.selectedItem = null;
        this.onModelChange(this.value);
        this.autocomplete.inputEL.nativeElement.value = '';
        this.clear.emit();
    }

    handleOnKeyUp(event) {
        if (this.autocomplete.overlayVisible) {
            let highlightedOptionIndex = -1;
            let highlightedElement;
            switch (event.key) {
                case ActionKeys.UP:
                    highlightedElement = document.getElementById('p-highlighted-option');
                    if (highlightedElement) {
                        if (highlightedElement.offsetTop < this.scrollPanel.scrollTop) {
                            this.scrollPanel.scrollTop = highlightedElement.offsetTop;
                        }
                    }
                    highlightedOptionIndex = this.autocomplete.findOptionIndex(this.autocomplete.highlightOption, this.autocomplete.suggestions);
                    if (highlightedOptionIndex === 0 && this.highlightedOptionIndex === 0) {
                        this.autocomplete.hide();
                        this.highlightedOptionIndex = -1;
                    } else {
                        this.highlightedOptionIndex = highlightedOptionIndex;
                    }
                    if (this.autocomplete.overlayVisible && this.isInTable) {
                        event.stopPropagation();
                    }
                    break;
                case ActionKeys.DOWN:
                    highlightedElement = document.getElementById('p-highlighted-option');
                    if (highlightedElement) {
                        const optionBottom = highlightedElement.offsetTop + highlightedElement.offsetHeight;
                        const scrollBottom = this.scrollPanel.scrollTop + 200;
                        if (optionBottom > scrollBottom) {
                            this.scrollPanel.scrollTop = this.scrollPanel.scrollTop + (optionBottom - scrollBottom);
                        }
                    }
                    highlightedOptionIndex = this.autocomplete.findOptionIndex(this.autocomplete.highlightOption, this.autocomplete.suggestions);
                    if (highlightedOptionIndex > -1) {
                        this.highlightedOptionIndex = highlightedOptionIndex;
                    }
                    if (this.autocomplete.overlayVisible && this.isInTable) {
                        event.stopPropagation();
                    }
                    break;
                case ActionKeys.HOME:
                    this.autocomplete.highlightOption = this.autocomplete.suggestions[0];
                    this.autocomplete.highlightOptionChanged = true;
                    break;
                case ActionKeys.END:
                    this.autocomplete.highlightOption = this.autocomplete.suggestions[this.autocomplete.suggestions.length - 1];
                    this.autocomplete.highlightOptionChanged = true;
                    break;
            }
        }

        // if user selects item from dropdown, we do not want to also add the partial search text with it.
        if (!this.selectedItemFromDropdown) {
            const searchString = (typeof this.selectedItem === 'string' || !this.selectedItem)
                ? this.selectedItem
                : this.selectedItem[this.labelField];

            this.searchStringChanged.emit(searchString);
            this.keyup.emit(event);
            event.stopPropagation();
        }
        if(event.key !== ActionKeys.UP && event.key !== ActionKeys.DOWN && event.key !== ActionKeys.LEFT && event.key !== ActionKeys.RIGHT) {
            this.selectedItemFromDropdown = null;

        }
    }

    onShow(event: any): void {
        this.autocomplete.inputEL?.nativeElement.setAttribute('aria-activedescendant', 'p-highlighted-option');
        this.autocomplete.inputEL?.nativeElement.setAttribute('aria-controls', this.autocomplete.listId);
        this.autocomplete.dropdownButton?.nativeElement.setAttribute('aria-controls', this.autocomplete.listId);
        this.autocomplete.dropdownButton?.nativeElement.setAttribute('aria-expanded', 'true');
        if (this.ariaLabel) {
            (document.querySelector('ul.p-autocomplete-items') as HTMLElement)?.setAttribute('aria-label', this.localizationService.transform(this.ariaLabel));
        }
        if (this.label) {
            (document.querySelector('ul.p-autocomplete-items') as HTMLElement)?.setAttribute('aria-labelledby', this.id + '_autocompleteLabel');
        }
        if (this.ariaLabelledBy) {
            (document.querySelector('ul.p-autocomplete-items') as HTMLElement)?.setAttribute('aria-labelledby', this.ariaLabelledBy);
        }

        this.scrollPanel = document.getElementsByClassName('p-autocomplete-panel')[0];

        this.show.emit(event);
    }

    onHide(event: any): void {
        this.autocomplete.inputEL?.nativeElement.setAttribute('aria-activedescendant', '');
        this.autocomplete.inputEL?.nativeElement.setAttribute('aria-controls', '');
        this.autocomplete.dropdownButton?.nativeElement.setAttribute('aria-controls', '');
        this.autocomplete.dropdownButton?.nativeElement.setAttribute('aria-expanded', 'false');
        this.hide.emit(event);
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // This will be needed in NEP-18100 when we move to prime17
    private findOptionIndex(option: any, suggestions: any[]): number {
        let index: number = -1;
        if (suggestions) {
            for (let i = 0; i < suggestions.length; i++) {
                if (isEqual(option, suggestions[i])) {
                    index = i;
                    break;
                }
            }
        }

        return index;
    }
}
